import React from 'react';

/**
 * Helper function to trim input value on blur while respecting optional pattern validation
 * @param event - The focus event from the input/textarea
 * @param onChange - Function to handle the value change after trimming
 * @param pattern - Optional regex pattern to validate against
 * @param onBlurCallback - Optional callback to run after trimming
 */
export const trimOnBlur = <T extends HTMLInputElement | HTMLTextAreaElement>(
	event: React.FocusEvent<T, Element>,
	onChange?: (value: string) => void,
	pattern?: string,
	onBlurCallback?: (event: React.FocusEvent<T, Element>) => void,
) => {
	if (event?.target) {
		const trimmedValue = event.target.value.trim();

		// Check if the trimmed value matches the pattern (if provided)
		const isValid = pattern ? new RegExp(pattern).test(trimmedValue) : true;

		// Only update if valid and if trimming actually changed something
		if (isValid && trimmedValue !== event.target.value) {
			event.target.value = trimmedValue;
			if (onChange) onChange(trimmedValue);
		}

		// Call original onBlur if provided
		onBlurCallback?.(event);
	}
};

/**
 * Helper function to trim input value on blur while respecting optional pattern validation
 * @param target - The event target
 * @param onChange - Function to handle the value change after trimming
 * @param pattern - Optional regex pattern to validate against
 */
export const trimOnEvent = <T extends HTMLInputElement | HTMLTextAreaElement>(
	target: T,
	onChange?: (value: string) => void,
	pattern?: string,
) => {
	if (target) {
		const trimmedValue = target.value.trim();

		if (pattern) {
			const matches = trimmedValue.match(pattern);
			let trimmed = '';
			matches?.forEach((m: string) => (trimmed += m));
			if (trimmed !== target.value) {
				target.value = trimmed;
				if (onChange) onChange(trimmed);
			}
		}
		// Only update if valid and if trimming actually changed something
		else if (trimmedValue !== target.value) {
			target.value = trimmedValue;
			if (onChange) onChange(trimmedValue);
		}
	}
};

/**
 * Handles input validation before the input is committed
 * @param e - The form event from the input
 * @param pattern - Optional regex pattern to validate against (can be string or RegExp)
 */
export const skipChangeByPatternInvalid = (
	e: React.FormEvent<HTMLInputElement>,
	pattern: string | RegExp,
) => {
	const input = e.target as HTMLInputElement;
	const currentValue = input.value;
	const selectionStart = input.selectionStart || 0;
	const selectionEnd = input.selectionEnd || 0;
	const key = (e as unknown as { data: string }).data; // The character being entered

	// Skip if no key (like backspace, delete, etc.)
	if (!key) return;

	// Calculate the new value that would result from this input
	const newValue =
		currentValue.substring(0, selectionStart) + key + currentValue.substring(selectionEnd);

	// Create the regex from the pattern
	const regex = typeof pattern === 'string' ? new RegExp(`^${pattern}$`) : pattern;

	// If the new value doesn't match the pattern, prevent the input
	const isValidByPattern = regex.test(newValue);
	if (newValue && !isValidByPattern) {
		e.preventDefault();
	}
};
